import { IconButton, Tooltip } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { DownloadGlyph } from '../icons';
import useMessageActionButtonStyles from '../messageActionButton.styles';
import useStyles from './ArtifactDownloadButton.styles';

const DOWNLOAD_ICON_SIZE_PX = 16;
const TOOLTIP_OFFSET = [0, -8];

/** Props for a chart or table artifact download action. */
export type ArtifactDownloadButtonProps = {
  /** Visible tooltip copy. */
  tooltip: string;
  /** Accessible name for the icon-only button. */
  ariaLabel: string;
  /** Starts the browser download and resolves after it is triggered. */
  onDownload: () => Promise<void> | void;
  /** Runs after the download is triggered without an immediate error. */
  onDownloadSuccess?: () => void;
  /** Runs when preparing or triggering the download fails. */
  onDownloadError?: () => void;
  disabled?: boolean;
  testId: string;
};

/**
 * Shared Figma-matched download control. Each instance owns its pending state,
 * so one chart download does not disable actions for sibling artifacts.
 */
function ArtifactDownloadButton({
  tooltip,
  ariaLabel,
  onDownload,
  onDownloadSuccess,
  onDownloadError,
  disabled = false,
  testId,
}: ArtifactDownloadButtonProps) {
  const { classes } = useStyles();
  const { classes: actionClasses, cx } = useMessageActionButtonStyles();
  const [isDownloading, setIsDownloading] = useState(false);
  const isDownloadLocked = useRef(false);
  const unlockTimeoutIdRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const isDisabled = disabled || isDownloading;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (unlockTimeoutIdRef.current !== null) {
        window.clearTimeout(unlockTimeoutIdRef.current);
        unlockTimeoutIdRef.current = null;
      }
    };
  }, []);

  const handleDownload = useCallback(async () => {
    if (disabled || isDownloadLocked.current) {
      return;
    }

    isDownloadLocked.current = true;
    setIsDownloading(true);
    let didTriggerDownload = false;
    try {
      await onDownload();
      didTriggerDownload = true;
    } catch (error) {
      console.error('[AI Sidekick] Artifact download failed.', error);
      onDownloadError?.();
    } finally {
      if (isMountedRef.current) {
        if (unlockTimeoutIdRef.current !== null) {
          window.clearTimeout(unlockTimeoutIdRef.current);
        }
        unlockTimeoutIdRef.current = window.setTimeout(() => {
          isDownloadLocked.current = false;
          setIsDownloading(false);
          unlockTimeoutIdRef.current = null;
        }, 0);
      }
    }

    if (didTriggerDownload) {
      try {
        onDownloadSuccess?.();
      } catch {
        // A host analytics hook cannot turn a completed browser download into
        // a user-facing failure.
      }
    }
  }, [disabled, onDownload, onDownloadError, onDownloadSuccess]);

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      describeChild
      classes={{ tooltip: classes.tooltip }}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: { offset: TOOLTIP_OFFSET },
            },
          ],
        },
      }}
    >
      <span className={classes.trigger}>
        <IconButton
          type="button"
          className={cx(actionClasses.button, classes.button)}
          onClick={() => void handleDownload()}
          disabled={isDisabled}
          aria-label={ariaLabel}
          aria-busy={isDownloading || undefined}
          data-testid={testId}
        >
          <DownloadGlyph size={DOWNLOAD_ICON_SIZE_PX} />
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default ArtifactDownloadButton;
